import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Amount, CustomSelect, TableSkeleton, btnSecondary } from '@redpanda/ui';
import { toIntlLocale, useRefetchOnFocus, useRememberedState } from '@redpanda/utils';
import { ComparisonRow, useComparison, yearOptions } from '../stores/reportStore';
import { useDataChanged } from '../utils/dataEvents';

type ReportType = 'income' | 'balance';
type Unit = 'month' | 'quarter' | 'year';
const TYPES: readonly ReportType[] = ['income', 'balance'];
const UNITS: readonly Unit[] = ['month', 'quarter', 'year'];
const COUNTS = ['2', '3', '4', '6'] as const;
type Count = typeof COUNTS[number];

const sectionTitleKey: Record<string, string> = {
  INCOME: 'reports.incomeAccounts', EXPENSE: 'reports.expenseAccounts',
  ASSET: 'reports.assetAccounts', LIABILITY: 'reports.liabilityAccounts', EQUITY: 'reports.equityAccounts',
};
const sectionTotalKey: Record<string, string> = {
  INCOME: 'reports.totalIncome', EXPENSE: 'reports.totalExpenses',
  ASSET: 'reports.totalAssets', LIABILITY: 'reports.totalLiabilities', EQUITY: 'reports.totalEquity',
};

/**
 * 期間比較：與前 N 期比較（同 Xero、QuickBooks）；每一欄一個月／季／年，最後一欄是和前一期的差異
 * 報表、單位、期數記住；比較到哪一期每次從本期開始
 */
const PeriodComparisonPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = toIntlLocale(i18n.language);
  const now = new Date();
  const [type, setType] = useRememberedState<ReportType>('accounting-ui:comparison.type', 'income', TYPES);
  const [unit, setUnit] = useRememberedState<Unit>('accounting-ui:comparison.unit', 'month', UNITS);
  const [count, setCount] = useRememberedState<Count>('accounting-ui:comparison.count', '3', COUNTS);
  const [endYear, setEndYear] = React.useState(now.getFullYear());
  const [endMonth, setEndMonth] = React.useState(now.getMonth() + 1);
  const end = unit === 'year' ? String(endYear) : `${endYear}-${String(endMonth).padStart(2, '0')}`;
  const query = { type, unit, end, count: Number(count) };
  const comparison = useComparison(query);

  useEffect(() => {
    comparison.refetch();
  }, [type, unit, end, count]); // eslint-disable-line react-hooks/exhaustive-deps
  useRefetchOnFocus(comparison.refetch);
  useDataChanged(['journals', 'accounts'], comparison.refetch);

  const periodLabel = (key: string) => {
    if (unit === 'year') return t('dashboard.yearLabel', { year: key });
    if (unit === 'quarter') {
      const [y, q] = key.split('-Q');
      return t('periodComparison.quarterLabel', { year: y, quarter: q });
    }
    const [y, m] = key.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(locale, { year: 'numeric', month: 'short' });
  };

  const data = comparison.data;
  const lastIndex = (data?.periods.length ?? 1) - 1;
  const diff = (values: number[]) => values[lastIndex] - values[lastIndex - 1];
  const cells = (values: number[], strong = false) => (
    <>
      {values.map((value, index) => (
        <td key={index} className={`py-2 px-3 w-36 text-right tabular-nums ${strong ? 'font-semibold text-fg' : ''}`}><Amount value={value} /></td>
      ))}
      <td className={`py-2 pl-3 pr-5 w-36 text-right tabular-nums ${strong ? 'font-semibold' : ''} text-fg-secondary`}>
        <Amount value={diff(values)} />
      </td>
    </>
  );
  const accountRow = (row: ComparisonRow) => (
    <tr key={row.id}>
      <td className="py-2 pl-5 pr-3 text-fg truncate"><span className="tabular-nums text-fg-secondary mr-2">{row.code}</span>{row.name}</td>
      {cells(row.amounts)}
    </tr>
  );
  const totalRow = (label: string, values: number[], strong = false) => (
    <tr className={strong ? 'border-t-2 border-line' : 'border-t border-line'}>
      <td className={`py-2.5 pl-5 pr-3 ${strong ? 'font-semibold text-fg' : 'font-medium text-fg'}`}>{label}</td>
      {cells(values, true)}
    </tr>
  );
  const isEmpty = !!data && data.sections.every(section => section.rows.length === 0);

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('periodComparison.title')}</h1>
          <button type="button" onClick={() => window.print()} disabled={!data || isEmpty} className={`${btnSecondary} disabled:opacity-50`}>{t('reports.print')}</button>
        </div>
      </div>

      {/* 條件列：改了就自動查（§3-8 查詢型報表不先給空白） */}
      <div className="mb-4 flex flex-col md:flex-row md:flex-wrap gap-3">
        <div className="md:w-40">
          <CustomSelect value={type} onChange={value => setType(value as ReportType)} options={TYPES.map(value => ({ value, label: t(`periodComparison.types.${value}`) }))} />
        </div>
        <div className="md:w-28">
          <CustomSelect value={unit} onChange={value => setUnit(value as Unit)} options={UNITS.map(value => ({ value, label: t(`periodComparison.units.${value}`) }))} />
        </div>
        <div className="md:w-32">
          <CustomSelect value={String(endYear)} onChange={value => setEndYear(Number(value))} options={yearOptions(6).map(y => ({ value: String(y), label: t('dashboard.yearLabel', { year: y }) }))} />
        </div>
        {unit !== 'year' && (
          <div className="md:w-28">
            <CustomSelect
              value={String(endMonth)}
              onChange={value => setEndMonth(Number(value))}
              options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: t(`common.time.months.${i + 1}`) }))}
            />
          </div>
        )}
        <div className="md:w-40">
          <CustomSelect value={count} onChange={value => setCount(value as Count)} options={COUNTS.map(value => ({ value, label: t('periodComparison.countLabel', { count: Number(value) }) }))} />
        </div>
      </div>

      <div className="report-print">
        <p className="print-only hidden text-lg font-semibold mb-2">{t('periodComparison.title')}・{t(`periodComparison.types.${type}`)}</p>
        {!data ? (
          <TableSkeleton rows={8} columns={Number(count) + 2} showHeader={true} />
        ) : isEmpty ? (
          <div className="bg-surface rounded-lg border border-line px-5 py-12 text-center text-sm text-fg-secondary">{t('periodComparison.noData')}</div>
        ) : (
          <div className="bg-surface rounded-lg border border-line overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: `${300 + (data.periods.length + 1) * 144}px` }}>
                <thead className="bg-gray-50 dark:bg-slate-800/60">
                  <tr className="text-[13px] font-semibold uppercase tracking-wider text-fg-secondary">
                    <th className="py-2 pl-5 pr-3 text-left font-semibold">{t('reports.columns.accountName')}</th>
                    {data.periods.map(period => <th key={period.key} className="py-2 px-3 text-right font-semibold whitespace-nowrap">{periodLabel(period.key)}</th>)}
                    <th className="py-2 pl-3 pr-5 text-right font-semibold whitespace-nowrap">{t('periodComparison.change')}</th>
                  </tr>
                </thead>
                {data.sections.map(section => (
                  <tbody key={section.type}>
                    <tr className="bg-gray-50/60 dark:bg-slate-800/30">
                      <td colSpan={data.periods.length + 2} className="py-2 px-5 text-[13px] font-semibold text-fg-secondary">{t(sectionTitleKey[section.type])}</td>
                    </tr>
                    {section.rows.map(accountRow)}
                    {section.type === 'EQUITY' && data.retainedEarnings && (
                      <tr>
                        <td className="py-2 pl-5 pr-3 text-fg">{t('reports.retainedEarnings')}</td>
                        {cells(data.retainedEarnings)}
                      </tr>
                    )}
                    {totalRow(
                      t(sectionTotalKey[section.type]),
                      section.type === 'EQUITY' && data.retainedEarnings ? section.totals.map((v, i) => v + data.retainedEarnings![i]) : section.totals,
                    )}
                  </tbody>
                ))}
                {data.netIncome && <tbody>{totalRow(t('reports.netIncome'), data.netIncome, true)}</tbody>}
              </table>
            </div>
          </div>
        )}
        <p className="mt-3 text-[13px] text-fg-secondary">{type === 'income' ? t('periodComparison.incomeHint') : t('periodComparison.balanceHint')}</p>
      </div>
    </div>
  );
};

export default PeriodComparisonPage;
